import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, Play } from "lucide-react";

/**
 * Speaking — "Stage Reel"
 * Wide cinematic stage hero with overlay + 3-tile thumbnail strip in varied
 * widths + impact stats.
 */

const HERO =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_hero-design-6/artifacts/olt9ce0r_Elegant%20Blue%20Podcast%20Studio%20Still%20Life.png";

const TILES = [
  {
    title: "Khutbah — When the heart forgets its Lord.",
    image: "https://images.unsplash.com/photo-1542816417-0983c9c9ad53?auto=format&fit=crop&w=900&q=80",
    meta: "32 MIN",
    href: "/speaking",
  },
  {
    title: "Fireside — Teachers as Tarbiyah Workers.",
    image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=900&q=80",
    meta: "48 MIN",
    href: "/speaking",
  },
  {
    title: "Conference — Raising Anchored Youth.",
    image: "https://images.unsplash.com/photo-1559223607-a43c990c692c?auto=format&fit=crop&w=900&q=80",
    meta: "26 MIN",
    href: "/speaking",
  },
];

/* Speaking summary section. */

const SpeakingSection = ({ section }) => {
  return (
    <section
      data-testid={`featured-section-${section.id}`}
      className="relative overflow-hidden bg-ink-deep"
    >
      {/* Ambient stage glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(74,143,229,0.14), transparent 65%), radial-gradient(ellipse 40% 40% at 10% 100%, rgba(74,143,229,0.06), transparent 70%)",
        }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-22">
        {/* Eyebrow + headline */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-10 lg:mb-12">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4">
              <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
                {section.eyebrow}
              </span>
              <span className="h-px w-10 bg-azure-soft/50" />
            </div>
            <h2 className="mt-9 font-serif text-white text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
              {section.headlineA}
            </h2>
            <h2 className="font-serif italic text-azure-soft text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em]">
              {section.headlineB}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-white/10">
            <p className="text-white/65 text-[14.5px] leading-[1.95] max-w-md">
              {section.intro}
            </p>
          </div>
        </div>

        {/* Cinematic stage hero */}
        <Link
          to={section.viewAllHref}
          data-testid={`featured-card-${section.id}-main`}
          className="group relative block aspect-[21/9] overflow-hidden border border-white/[0.07] hover:border-azure/40 transition-colors duration-700"
        >
          <img
            src={HERO}
            alt="Microphone, headphones, and books in a warmly lit blue recording studio."
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
          />
          {/* Cinematic gradients — bottom darkening + left fade */}
          <div className="absolute inset-0 bg-gradient-to-t from-ink-deep via-ink-deep/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-deep/70 via-transparent to-ink-deep/30" />
          {/* Spotlight bloom */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle 600px at 70% 30%, rgba(74,143,229,0.25), transparent 65%)",
              mixBlendMode: "screen",
            }}
          />

          <div className="absolute top-8 left-8 inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-azure-soft">
            <Mic className="w-4 h-4" strokeWidth={1.6} />
            Featured Keynote
          </div>

          <div className="absolute left-8 right-8 bottom-8 max-w-3xl">
            <h3 className="font-serif text-white text-[24px] sm:text-[30px] lg:text-[38px] leading-[1.05] tracking-[-0.015em]">
              Raising a Generation
              <span className="italic text-azure-soft"> Anchored. Awakened. Advancing.</span>
            </h3>
            <div className="mt-7 flex items-center gap-5">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 backdrop-blur border border-white/40 text-white group-hover:bg-azure group-hover:border-azure transition-colors duration-500">
                <Play className="w-4 h-4 fill-current" strokeWidth={0} />
              </span>
              <span className="text-[11px] tracking-[0.34em] uppercase text-white">Watch the Keynote</span>
              <span className="text-[11px] tracking-[0.30em] uppercase text-white/45">42 MIN</span>
            </div>
          </div>
        </Link>

        {/* Thumbnail strip with organic widths */}
        <div className="mt-10 lg:mt-12 grid grid-cols-12 gap-5 lg:gap-6">
          {TILES.map((t, i) => {
            const colSpan = i === 0 ? "lg:col-span-5" : i === 1 ? "lg:col-span-4" : "lg:col-span-3";
            return (
              <Link
                key={t.title}
                to={t.href}
                data-testid={`featured-card-${section.id}-${i}`}
                className={`group relative col-span-12 sm:col-span-4 ${colSpan} block overflow-hidden border border-white/[0.07] hover:border-azure/40 transition-colors duration-700`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={t.image}
                    alt={t.title}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-deep via-ink-deep/30 to-transparent" />
                  <span className="absolute top-5 right-5 text-[10px] tracking-[0.30em] uppercase text-white/70">
                    {t.meta}
                  </span>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-11 h-11 rounded-full bg-white/10 backdrop-blur border border-white/40 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                      <Play className="w-3.5 h-3.5 text-white fill-current" strokeWidth={0} />
                    </span>
                  </div>
                </div>
                <div className="absolute left-5 right-5 bottom-5">
                  <h4 className="font-serif text-white text-[18px] lg:text-[20px] leading-[1.25] tracking-[-0.005em]">
                    {t.title}
                  </h4>
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 lg:mt-14 pt-10 border-t border-white/10 flex justify-end">
          <Link
            to={section.viewAllHref}
            data-testid={`featured-view-all-${section.id}`}
            className="group inline-flex items-center gap-3 text-[11px] tracking-[0.34em] uppercase text-azure-soft hover:text-azure border-b border-azure-soft/60 hover:border-azure pb-2.5 transition-colors"
          >
            Book Iyare to Speak
            <ArrowRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SpeakingSection;
